import * as React from "react";
import { useState } from "react";
import { Spinner } from "@finster/ui";
import { cn } from "@finster/ui";
import { ChevronDown, ChevronUp, ArrowLeft } from "lucide-react";
import { FinsterTask, TaskRun, getTaskOutput } from "../finsterApi";
import { extractTaskOutputCitations, extractTaskOutputText } from "../services/taskGrounding";
import { SimpleMarkdown } from "./SimpleMarkdown";
import type { Citation } from "../citations";

interface TasksHomeProps {
  tasks: FinsterTask[];
  loading: boolean;
  error: string | null;
  userEmail: string;
  onUseForDeck?: (content: string, taskName: string, citations?: Citation[]) => void;
}

export const TasksHome: React.FC<TasksHomeProps> = ({
  tasks,
  loading,
  error,
  userEmail,
  onUseForDeck,
}) => {
  const [expandedTaskId, setExpandedTaskId] = useState<string | null>(null);
  const [selectedRun, setSelectedRun] = useState<{ task: FinsterTask; run: TaskRun } | null>(null);
  const [taskOutput, setTaskOutput] = useState<string | null>(null);
  const [taskOutputCitations, setTaskOutputCitations] = useState<Citation[] | undefined>(undefined);
  const [outputLoading, setOutputLoading] = useState(false);

  const handleTaskClick = (task: FinsterTask) => {
    setExpandedTaskId(expandedTaskId === task.taskId ? null : task.taskId);
  };

  const handleRunClick = async (e: React.MouseEvent, task: FinsterTask, run: TaskRun) => {
    e.stopPropagation();
    setSelectedRun({ task, run });
    setOutputLoading(true);
    setTaskOutput(null);
    setTaskOutputCitations(undefined);
    try {
      const result = await getTaskOutput(userEmail, task.taskId, run.taskRunId);
      if (result.error) {
        setTaskOutput(`**Error:** ${result.error}`);
      } else {
        setTaskOutputCitations(extractTaskOutputCitations(result.data));
        setTaskOutput(extractTaskOutputText(result.data) || JSON.stringify(result.data, null, 2));
      }
    } catch (err) {
      setTaskOutput(`**Error:** ${err}`);
    } finally {
      setOutputLoading(false);
    }
  };

  const handleBack = () => {
    setSelectedRun(null);
    setTaskOutput(null);
    setTaskOutputCitations(undefined);
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "";
    try {
      return new Date(dateStr).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  const getStateClasses = (state?: string) => {
    switch (state?.toUpperCase()) {
      case "READY":
      case "COMPLETE":
      case "COMPLETED":
        return "bg-primary/10 text-primary";
      case "RUNNING":
      case "PENDING":
        return "bg-yellow-500/10 text-yellow-600";
      case "ERROR":
      case "FAILED":
        return "bg-destructive/10 text-destructive";
      default:
        return "bg-primary/10 text-primary";
    }
  };

  if (selectedRun) {
    return (
      <div className="flex flex-col flex-1 min-h-0 min-w-0 bg-background overflow-hidden">
        {/* Navigation bar */}
        <div className="px-4 py-2.5 border-b border-border bg-card flex items-center justify-between shrink-0">
          <button 
            onClick={handleBack} 
            className="flex items-center gap-1 text-muted-foreground hover:text-foreground cursor-pointer text-[13px] font-medium rounded transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back</span>
          </button>
          {onUseForDeck && taskOutput && !outputLoading && (
            <button
              className="bg-primary text-primary-foreground border-none rounded-md px-3 py-1.5 text-[12px] font-medium cursor-pointer flex items-center gap-1.5 hover:opacity-90 transition-opacity"
              onClick={() => {
                onUseForDeck(taskOutput, selectedRun.run.taskRunName || selectedRun.task.taskName, taskOutputCitations);
              }}
            >
              <span>✨</span>
              <span>Use for Deck</span>
            </button>
          )}
        </div>

        {/* Task info header */}
        <div className="px-4 py-3 border-b border-border bg-card shrink-0">
          <h3 className="text-[15px] font-semibold text-foreground m-0 leading-tight">
            {selectedRun.run.taskRunName || `Run ${selectedRun.run.taskRunId.slice(-6)}`}
          </h3>
          <p className="text-[12px] text-muted-foreground mt-1 m-0">
            {selectedRun.task.taskName} • {formatDate(selectedRun.run.taskRunTimestamp)}
          </p>
        </div>

        {/* Content area */}
        <div
          className="flex-1 px-4 py-4 overflow-y-auto overflow-x-hidden text-[13px] leading-relaxed text-foreground"
          style={{ minWidth: 0, maxWidth: "100%", width: "100%" }}
        >
          {outputLoading ? (
            <div className="flex items-center justify-center py-12 gap-2 text-muted-foreground text-sm">
              <Spinner size="sm" />
              <span>Loading output...</span>
            </div>
          ) : taskOutput ? (
            <div style={{ maxWidth: "100%", overflow: "hidden" }}>
              <SimpleMarkdown text={taskOutput} />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground text-[13px] text-center">
              <span>No output available</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="px-5 py-4 border-b border-border bg-card shrink-0">
        <h2 className="text-base font-semibold text-foreground m-0 mb-1">Your Tasks</h2>
        <p className="text-xs text-muted-foreground">
          {tasks.length} task{tasks.length !== 1 ? "s" : ""} • Click to expand runs
        </p>
      </div>

      {loading && (
        <div className="flex items-center justify-center p-5 gap-2 text-muted-foreground text-xs">
          <Spinner size="sm" />
          Loading tasks...
        </div>
      )}

      {error && (
        <div className="flex flex-col items-center justify-center p-10 text-muted-foreground text-[13px] text-center">
          <span>⚠️ {error}</span>
        </div>
      )}

      {!loading && !error && tasks.length === 0 && (
        <div className="flex flex-col items-center justify-center p-10 text-muted-foreground text-[13px] text-center">
          <span className="text-2xl mb-2">📋</span>
          <span>No tasks found</span>
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-3">
        {tasks.map((task) => {
          const isExpanded = expandedTaskId === task.taskId;
          const hasRuns = task.taskRuns && task.taskRuns.length > 0;
          return (
            <div
              key={task.taskId}
              className={cn(
                "bg-card rounded-lg border border-border mb-2 overflow-hidden",
                isExpanded && "border-primary"
              )}
            >
              <div
                className={cn(
                  "px-3.5 py-3 cursor-pointer flex justify-between items-start transition-colors",
                  isExpanded && "bg-accent"
                )}
                onClick={() => handleTaskClick(task)}
              >
                <div className="flex-1">
                  <div className="text-[13px] font-medium text-foreground mb-1 leading-tight">{task.taskName}</div>
                  {task.taskTopic && task.taskTopic !== "None" && (
                    <div className="text-[11px] text-muted-foreground mb-2 overflow-hidden text-ellipsis whitespace-nowrap">{task.taskTopic}</div>
                  )}
                  <div className="flex items-center gap-2 flex-wrap">
                    {task.taskState && (
                      <span className={cn("text-[10px] px-1.5 py-0.5 rounded font-medium uppercase", getStateClasses(task.taskState))}>
                        {task.taskState}
                      </span>
                    )}
                    {task.lastRun && (
                      <span className="text-[10px] text-muted-foreground">{formatDate(task.lastRun)}</span>
                    )}
                    {hasRuns && (
                      <span className="text-[10px] text-muted-foreground">
                        • {task.taskRuns!.length} run{task.taskRuns!.length !== 1 ? "s" : ""}
                      </span>
                    )}
                  </div>
                </div>
                {hasRuns && (
                  <div className="text-muted-foreground shrink-0 ml-2 mt-0.5">
                    {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </div>
                )}
              </div>

              {isExpanded && hasRuns && (
                <div className="border-t border-border bg-accent max-h-[200px] overflow-y-auto">
                  {task.taskRuns!.map((run) => (
                    <div
                      key={run.taskRunId}
                      className="px-3.5 py-2.5 border-b border-border cursor-pointer flex justify-between items-center text-xs hover:bg-primary-muted transition-colors"
                      onClick={(e) => handleRunClick(e, task, run)}
                    >
                      <span className="text-foreground font-medium text-[11px]">
                        {run.taskRunName || `Run ${run.taskRunId.slice(-6)}`}
                      </span>
                      <div className="flex gap-2 items-center">
                        {run.taskRunState && (
                          <span className={cn("text-[9px] px-1.5 py-0.5 rounded font-medium uppercase", getStateClasses(run.taskRunState))}>
                            {run.taskRunState}
                          </span>
                        )}
                        {run.taskRunTimestamp && (
                          <span className="text-[10px] text-muted-foreground">{formatDate(run.taskRunTimestamp)}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
